import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, of, retry, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from './models';

/**
 * ApiService provides typed HTTP methods for the Tenant Portal.
 *
 * Uses the environment `apiUrl` as the base URL for all requests.
 * The Bearer token is attached by the `authInterceptor` (registered in app config).
 * Handles network errors and server errors with user-friendly messages.
 *
 * Requirements: 9.5, 7.5
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);

  /** Generate a short correlation ID for request tracing. */
  private newRequestId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  }

  /** Build headers with X-Request-Id for correlation. */
  private buildHeaders(requestId: string): HttpHeaders {
    return new HttpHeaders({ 'X-Request-Id': requestId });
  }

  get<T>(path: string, params?: Record<string, string>): Observable<ApiResponse<T>> {
    const requestId = this.newRequestId();
    console.debug(`[ApiService] GET ${path} | requestId=${requestId}`);

    return this.http
      .get<ApiResponse<T>>(`${environment.apiUrl}${path}`, {
        params,
        headers: this.buildHeaders(requestId),
      })
      .pipe(
        retry({
          count: 2,
          delay: (error: HttpErrorResponse, retryCount: number) => {
            if (error.status >= 500 && error.status < 600) {
              const delayMs = Math.pow(2, retryCount) * 200;
              return timer(delayMs);
            }
            throw error;
          },
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(`[ApiService] GET ${path} failed | requestId=${requestId}`, err);
          return this.handleError<T>(err);
        }),
      );
  }

  post<T>(path: string, body: unknown = {}): Observable<ApiResponse<T>> {
    const requestId = this.newRequestId();
    console.debug(`[ApiService] POST ${path} | requestId=${requestId}`);

    return this.http
      .post<ApiResponse<T>>(`${environment.apiUrl}${path}`, body, {
        headers: this.buildHeaders(requestId),
      })
      .pipe(
        retry({
          count: 1,
          delay: (error: HttpErrorResponse) => {
            // POST is not idempotent — only retry on network errors (status 0)
            if (error.status === 0) {
              return timer(200);
            }
            throw error;
          },
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(`[ApiService] POST ${path} failed | requestId=${requestId}`, err);
          return this.handleError<T>(err);
        }),
      );
  }

  patch<T>(path: string, body: unknown = {}): Observable<ApiResponse<T>> {
    const requestId = this.newRequestId();
    console.debug(`[ApiService] PATCH ${path} | requestId=${requestId}`);

    return this.http
      .patch<ApiResponse<T>>(`${environment.apiUrl}${path}`, body, {
        headers: this.buildHeaders(requestId),
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          console.error(`[ApiService] PATCH ${path} failed | requestId=${requestId}`, err);
          return this.handleError<T>(err);
        }),
      );
  }

  put<T>(path: string, body: unknown = {}): Observable<ApiResponse<T>> {
    const requestId = this.newRequestId();
    console.debug(`[ApiService] PUT ${path} | requestId=${requestId}`);

    return this.http
      .put<ApiResponse<T>>(`${environment.apiUrl}${path}`, body, {
        headers: this.buildHeaders(requestId),
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          console.error(`[ApiService] PUT ${path} failed | requestId=${requestId}`, err);
          return this.handleError<T>(err);
        }),
      );
  }

  delete<T>(path: string): Observable<ApiResponse<T>> {
    const requestId = this.newRequestId();
    console.debug(`[ApiService] DELETE ${path} | requestId=${requestId}`);

    return this.http
      .delete<ApiResponse<T>>(`${environment.apiUrl}${path}`, {
        headers: this.buildHeaders(requestId),
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          console.error(`[ApiService] DELETE ${path} failed | requestId=${requestId}`, err);
          return this.handleError<T>(err);
        }),
      );
  }

  private handleError<T>(err: HttpErrorResponse): Observable<ApiResponse<T>> {
    if (err.status === 0) {
      // Network error — no response received from server
      return of({
        ok: false,
        error: 'Unable to reach the server. Please check your connection.',
        status: 0,
      });
    }
    if (err.status === 429) {
      return of({
        ok: false,
        error: 'Too many requests. Please wait and try again.',
        status: 429,
      });
    }
    // Server returned a non-2xx status — extract error from body if present
    const body = err.error as ApiResponse<T> | null;
    return of({
      ok: false,
      error: body?.error ?? err.statusText,
      status: err.status,
    });
  }
}
